import { inject, Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { GetUserResponse, InstagramApiService, MessagingStatus } from '@vendasta/instagram';
import { GetMessagingStatusResponseInterface } from '@vendasta/instagram/lib/_internal/interfaces/messaging.interface';
import { User } from '@vendasta/instagram/lib/_internal/objects/user';
import { firstValueFrom, map, Observable, shareReplay, switchMap } from 'rxjs';
import { PARTNER_ID_TOKEN } from '../../../../core/src/lib/tokens';
import { SocialPageInstagram } from '../interfaces';

export interface InstagramPageConnectionInfo {
  name: string;
  profileUrl?: string;
  status: MessagingStatus;
  statusCheckPermissionError?: boolean;
  internalInstagramUserId?: string;
}

@Injectable({
  providedIn: 'any',
})
export class InstagramMessagesService {
  private readonly instagramAPIService = inject(InstagramApiService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly partnerID$ = inject(PARTNER_ID_TOKEN);

  enableMessaging(
    instagramPageData: Pick<SocialPageInstagram, 'partnerId' | 'businessId' | 'internalInstagramUserId'>,
  ): Observable<unknown> {
    return this.instagramAPIService.enableMessaging({
      partnerId: instagramPageData.partnerId,
      businessId: instagramPageData.businessId,
      userId: instagramPageData.internalInstagramUserId,
    });
  }

  async redirectToFacebookReAuth(accountID: string, userID: string): Promise<void> {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}`;
    const nextUrl: URL = new URL(baseUrl);
    nextUrl.pathname = `/account/location/${accountID}/settings/inbox`;

    const partnerID = await firstValueFrom(this.partnerID$);
    const env = this.environmentService.getEnvironment() === Environment.PROD ? 'prod' : 'demo';
    window.location.href =
      `https://instagram-${env}.apigateway.co/v2/auth-user` +
      `?partnerID=${partnerID}` +
      `&businessID=${accountID}` +
      `&userID=${userID}` +
      `&nextURL=${nextUrl.href}`;
  }

  async redirectToFacebookAuth(accountID: string): Promise<void> {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}`;
    const nextUrl: URL = new URL(baseUrl);
    nextUrl.pathname = `/account/location/${accountID}/settings/connections/target/inbox`;

    const partnerID = await firstValueFrom(this.partnerID$);
    const env = this.environmentService.getEnvironment() === Environment.PROD ? 'prod' : 'demo';
    window.location.href =
      `https://instagram-${env}.apigateway.co/v2/auth-user` +
      `?partnerID=${partnerID}` +
      `&businessID=${accountID}` +
      `&nextURL=${nextUrl.href}`;
  }

  disableMessaging(instagramUserId: string): Observable<unknown> {
    return this.instagramAPIService.disableMessaging({ instagramUserId });
  }

  listInstagramUsers(accountGroupId: string): Observable<SocialPageInstagram[]> {
    return this.partnerID$.pipe(
      switchMap((partnerID) => {
        return this.instagramAPIService.listUsers({
          partnerId: partnerID,
          businessId: accountGroupId,
          options: {
            pageSize: 100,
          },
        });
      }),
      map((response) => {
        return response.users.map(userToSocialPageInstagram);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getInstagramUser(accountGroupId: string, userId: string): Observable<SocialPageInstagram> {
    return this.partnerID$.pipe(
      switchMap((partnerID) => {
        return this.instagramAPIService.getUser({
          partnerId: partnerID,
          businessId: accountGroupId,
          userId: userId,
        });
      }),
      map((response: GetUserResponse) => userToSocialPageInstagram(response.user)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  getMessagingStatus(partnerId: string, accountId: string): Observable<GetMessagingStatusResponseInterface> {
    return this.instagramAPIService.getMessagingStatus({
      partnerId,
      businessId: accountId,
    });
  }
}

function userToSocialPageInstagram(user: User): SocialPageInstagram {
  return {
    type: 'instagram',
    name: user.fullName,
    username: user.username,
    facebookPageId: user.facebookUserId,
    instagramUserId: user.instagramUserId,
    internalInstagramUserId: user.userId,
    partnerId: user.partnerId,
    businessId: user.businessId,
    profileUrl: `https://www.instagram.com/${user.username}/`,
    // TODO(BREW-687): Can we populate any of these? Are any relevant?
    isVerified: true,
    locationId: '',
    mapsUri: '',
    ssid: '',
    pages: [],
    clientTags: [],
    deleteUrl: '',
    tokenIsBroken: false,
  };
}
